import React from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { MonoButton } from './components'
import { clearPersisted, loadPersisted } from './persistence'

export type RequestExit = (onConfirm: () => void) => void

export const useExitConfirm = (): {
  requestExit: RequestExit
  dialog: React.ReactNode
} => {
  const [pending, setPending] = React.useState<(() => void) | null>(null)

  // Data persists across close/reopen; only an active card hold warrants a warning.
  const requestExit = React.useCallback<RequestExit>((onConfirm) => {
    if (!loadPersisted()?.paymentAuthorized) {
      onConfirm()
      return
    }
    setPending(() => onConfirm)
  }, [])

  const stay = () => setPending(null)
  const leave = () => {
    clearPersisted()
    const action = pending
    setPending(null)
    action?.()
  }

  const dialog = (
    <Dialog
      open={!!pending}
      onClose={stay}
      sx={{ zIndex: 1400 }}
      maxWidth="xs"
      slotProps={{ paper: { sx: { borderRadius: '6px', p: 1 } } }}
    >
      <DialogTitle sx={{ fontWeight: 900, textTransform: 'uppercase', pb: 1 }}>
        Leave checkout?
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 2 }}>
        <DialogContentText sx={{ fontSize: 14 }}>
          Closing now will lose your progress. The hold on your card will be
          released automatically after 7 days.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <MonoButton
          variant="text"
          onClick={leave}
          sx={{
            color: 'error.main',
            borderBottomColor: 'error.main',
            fontSize: 11,
            py: 1,
            px: 1.5,
          }}
        >
          Leave
        </MonoButton>
        <MonoButton
          variant="contained"
          color="primary"
          onClick={stay}
          sx={{ fontSize: 11, px: 3 }}
        >
          Stay
        </MonoButton>
      </DialogActions>
    </Dialog>
  )

  return { requestExit, dialog }
}
